<div class="irembogov-table-with-input">
  <p *ngIf="field.props?.placeholder" class="text-muted small mb-2">
    {{ field.props?.placeholder ?? '' | translate }}
  </p>

  <div
    class="d-flex gap-2 mb-2"
    *ngIf="
      !isReadOnly && (showSearch || (enableRowSelection && selectedRows.length))
    ">
    <div class="search-box flex-fill" *ngIf="showSearch">
      <i class="fa-solid fa-magnifying-glass search-box-icon"></i>
      <input
        type="text"
        class="form-control"
        [placeholder]="'Search' | translate"
        [attr.aria-label]="'Search' | translate"
        [(ngModel)]="searchTerm" />
    </div>
    <button
      *ngIf="enableRowSelection && selectedRows.length"
      type="button"
      class="btn btn-bulk-delete flex-shrink-0"
      (click)="bulkDelete()">
      <i class="fa-solid fa-trash-can"></i> {{ 'Delete' | translate }} ({{
        selectedRows.length
      }})
    </button>
  </div>

  <div class="table-with-input-block">
    <irembogov-data-table
      customTableClass="table-with-input"
      [columns]="headerColumns"
      [data]="filteredRows"
      [searchable]="false"
      [selectable]="!isReadOnly && enableRowSelection"
      [pageSize]="showPagination ? itemsPerPage : rows.length || 1"
      [collectionSize]="filteredRows.length"
      [minimalPagination]="!showPagination"
      [emptyMessage]="field.props?.['emptyMessage'] ?? 'No data available'"
      (selectionChanged)="onSelectionChanged($event)">
      <ng-template
        let-row
        let-index="index"
        let-selected="selected"
        let-toggleSelect="toggleSelect">
        <tr [class.selected-row]="selected">
          <td class="select-cell" *ngIf="!isReadOnly && enableRowSelection">
            <input
              type="checkbox"
              [attr.aria-label]="'Select row' | translate"
              [checked]="selected"
              (click)="toggleSelect()"
              (keydown)="toggleSelect()" />
          </td>
          <td
            *ngFor="let column of tableColumns"
            [title]="formatCellValue(row, column)">
            {{ formatCellValue(row, column) }}
          </td>
          <td
            class="text-end actions-cell"
            *ngIf="!isReadOnly && (enableEdit || enableDeletion)">
            <button
              *ngIf="enableEdit"
              type="button"
              class="btn btn-link btn-sm"
              [attr.aria-label]="'Edit item' | translate"
              (click)="openEditPanel(row)">
              <i class="fa-solid fa-pen"></i>
            </button>
            <button
              *ngIf="enableDeletion"
              type="button"
              class="btn btn-link btn-sm text-danger"
              [attr.aria-label]="'Delete item' | translate"
              (click)="deleteRow(row)">
              <i class="fa-solid fa-trash-can"></i>
            </button>
          </td>
        </tr>
      </ng-template>
    </irembogov-data-table>

    <span *ngIf="rows.length" class="item-count-badge">
      {{ rows.length }} {{ (rows.length === 1 ? 'item' : 'items') | translate }}
    </span>

    <button
      *ngIf="!isReadOnly && !panelOpen"
      type="button"
      class="btn btn-outline-primary btn-add-item w-100"
      [disabled]="isMaxItemsReached || formControl.disabled"
      (click)="openAddPanel()">
      <i class="fa-solid fa-plus"></i> {{ 'Add Item' | translate }}
    </button>

    <div class="input-block p-3" *ngIf="panelOpen">
      <p class="input-block-title mb-3">
        {{ (panelMode === 'add' ? 'Add new item' : 'Edit item') | translate }}
      </p>
      <form [formGroup]="rowForm">
        <div class="mb-3" *ngFor="let column of columns">
          <label class="form-label" [attr.for]="column.key">
            {{ column.props?.label ?? '' | translate }}
            <span *ngIf="column.props?.required" class="text-danger">*</span>
          </label>

          <ng-container [ngSwitch]="columnKind(column)">
            <input
              *ngSwitchCase="'text'"
              type="text"
              class="form-control"
              [id]="column.key"
              [formControlName]="column.key"
              [placeholder]="column.props?.placeholder || '' | translate" />
            <input
              *ngSwitchCase="'number'"
              type="number"
              class="form-control"
              [id]="column.key"
              [formControlName]="column.key"
              [placeholder]="column.props?.placeholder || '' | translate" />
            <input
              *ngSwitchCase="'email'"
              type="email"
              class="form-control"
              [id]="column.key"
              [formControlName]="column.key"
              [placeholder]="column.props?.placeholder || '' | translate" />
            <irembogov-irembo-date-picker
              *ngSwitchCase="'date'"
              [id]="column.key"
              [formControlName]="column.key"
              [placeholder]="
                column.props?.placeholder || 'Select Date' | translate
              "
              [maxDate]="column.props?.['maxDate']"
              [minDate]="column.props?.['minDate']"
              [dateFormat]="column.props?.['dateFormat']"
              [delimeter]="column.props?.['delimeter']"
              [eventCode]="column.props?.['eventCode']"
              [eventDatesMaxSize]="column.props?.['eventDatesMaxSize']"
              [addRemoveTimeMinDate]="column.props?.['addRemoveTimeMinDate']"
              [addRemoveTimeMaxDate]="column.props?.['addRemoveTimeMaxDate']"
              [allowedDaysOfTheWeek]="column.props?.['allowedDaysOfTheWeek']"
              [excludedDates]="column.props?.['excludedDates']">
            </irembogov-irembo-date-picker>
            <ng-container *ngSwitchCase="'select'">
              <!-- Angular picks SelectControlValueAccessor vs.
                   SelectMultipleControlValueAccessor based on the
                   `multiple` attribute being structurally present in the
                   template, not on its bound value — so single- and
                   multi-select need two separate <select> elements. -->
              <select
                *ngIf="!column.props?.multiple"
                class="form-select"
                [id]="column.key"
                [formControlName]="column.key">
                <option [ngValue]="null" disabled>
                  {{ column.props?.placeholder || 'Select' | translate }}
                </option>
                <option
                  *ngFor="let opt of column.props?.options"
                  [ngValue]="optionValue(column, opt)">
                  {{ optionLabel(column, opt) | translate }}
                </option>
              </select>
              <select
                *ngIf="column.props?.multiple"
                multiple
                class="form-select"
                [id]="column.key"
                [formControlName]="column.key">
                <option
                  *ngFor="let opt of column.props?.options"
                  [ngValue]="optionValue(column, opt)">
                  {{ optionLabel(column, opt) | translate }}
                </option>
              </select>
            </ng-container>
            <div *ngSwitchCase="'checkbox'" class="form-check form-switch">
              <input
                type="checkbox"
                class="form-check-input"
                [id]="column.key"
                [formControlName]="column.key" />
            </div>
          </ng-container>

          <div
            class="text-danger small mt-1"
            *ngIf="
              rowForm.get(column.key)?.touched &&
              rowForm.get(column.key)?.hasError('required')
            ">
            {{ requiredErrorMessage(column) | translate }}
          </div>
          <div
            class="text-danger small mt-1"
            *ngIf="
              rowForm.get(column.key)?.touched &&
              rowForm.get(column.key)?.hasError('email')
            ">
            {{ 'Please enter a valid email address' | translate }}
          </div>
          <div
            class="text-danger small mt-1"
            *ngIf="getUniquenessError(column.key)">
            {{ getUniquenessError(column.key) ?? '' | translate }}
          </div>
        </div>
      </form>

      <div class="d-flex gap-3 mt-2">
        <button
          type="button"
          class="btn btn-cancel flex-fill"
          (click)="cancelPanel()">
          {{ (panelMode === 'add' ? 'Cancel' : 'Discard Changes') | translate }}
        </button>
        <button
          type="button"
          class="btn btn-primary flex-fill"
          [disabled]="
            rowForm.invalid || (panelMode === 'edit' && rowForm.pristine)
          "
          (click)="onSave()">
          <i class="fa-solid fa-circle-check me-1"></i>
          {{ (panelMode === 'add' ? 'Save' : 'Save changes') | translate }}
        </button>
      </div>
    </div>
  </div>

  <p
    class="text-danger small mt-2"
    *ngIf="!isReadOnly && !panelOpen && isMaxItemsReached">
    {{
      'You have reached the maximum number of items you can add in the table'
        | translate
    }}
    ({{ rows.length }})
  </p>
</div>

<ng-template #confirmModalTpl let-modal>
  <div class="modal-body d-flex gap-3">
    <div
      class="confirm-icon"
      [class.confirm-icon--warning]="confirmVariant === 'warning'">
      <i
        class="fa-solid"
        [class.fa-trash-can]="confirmVariant === 'danger'"
        [class.fa-triangle-exclamation]="confirmVariant === 'warning'"></i>
    </div>
    <div>
      <h5 class="mb-2">{{ confirmTitle }}</h5>
      <p
        class="mb-0 text-muted"
        *ngIf="confirmMessageValue !== null; else plainConfirmMessage">
        {{ confirmMessagePrefix }}<strong>{{ confirmMessageValue }}</strong
        >{{ confirmMessageSuffix }}
      </p>
      <ng-template #plainConfirmMessage>
        <p class="mb-0 text-muted">{{ confirmMessage }}</p>
      </ng-template>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-cancel" (click)="modal.dismiss()">
      {{ confirmCancelLabel }}
    </button>
    <button
      type="button"
      class="btn btn-danger"
      (click)="onConfirmAccept(); modal.close()">
      {{ confirmAcceptLabel }}
    </button>
  </div>
</ng-template>
